import { Box, CircularProgress, List, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchAvailableIntegrations, fetchIntegrations } from '@sophocles/api/customer';

import IntegrationListItem from './IntegrationListItem';

const IntegrationList = ({ customerId }) => {
  const { t } = useTranslation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [available, setAvailable] = useState([]);
  const [adding, setAdding] = useState({});

  const load = async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    const [current, avail] = await Promise.all([fetchIntegrations(customerId), fetchAvailableIntegrations(customerId)]);
    setIntegrations(current);
    setAvailable(avail);
    if (isInitial) setInitialLoading(false);
  };

  useEffect(() => {
    load(true);
  }, [customerId]);

  // Filter available integrations to only those not already added
  const addedIds = new Set(integrations.map(i => i.integration.id));
  const notAdded = available.filter(a => !addedIds.has(a.id));

  // Merge available integrations into the list as disabled, not yet added
  const mergedIntegrations = [
    ...integrations,
    ...notAdded.map(a => ({
      enabled: false,
      id: null,
      integration: a,
    })),
  ];

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (mergedIntegrations.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('settings.noIntegrations', 'No integrations configured.')}
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {mergedIntegrations.map((integration, idx) => (
        <IntegrationListItem
          key={integration.integration.id}
          integration={integration}
          customerId={customerId}
          onChange={load}
          // Skip the divider on the last row — bordered Subsection
          // already provides the bottom edge.
          divider={idx < mergedIntegrations.length - 1}
        />
      ))}
    </List>
  );
};

export default IntegrationList;
